import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, HostListener, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Event, Router, Scroll } from '@angular/router';
import { CejModalComponent } from '@core/components/cej-modal/cej-modal.component';
import { ContactUsComponent } from '@core/components/contact-us/contact-us.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { WindowResizeService } from '@core/services/window-resize/window-resize.service';
import { environment } from '@environment';
import { RouterState } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { PopupEventState } from '@stores/popup-event/popup-event.state';
import { WINDOW } from '@wizbii/angular-utilities';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, take } from 'rxjs/operators';

@Component({
  selector: 'app-core',
  templateUrl: './core.component.html',
  styleUrls: ['./core.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreComponent implements OnInit {
  /**
   * Use class `hover-on` in CSS as follows:
   * `:host-context(.hover-on) .link:hover { ... }`
   */
  @HostBinding('class.hover-on') hoverEnabled = true;

  @HostBinding('class.accessibility-on') accessibilityEnabled = true;

  @HostBinding('class.hover-off')
  get hoverDisabled(): boolean {
    return !this.hoverEnabled;
  }

  @HostBinding('class.accessibility-off')
  get accessibilityDisabled(): boolean {
    return !this.accessibilityEnabled;
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  @Select(PopupEventState.shouldDisplay)
  shouldDisplayModal$!: Observable<boolean>;

  displayFullscreen$!: Observable<boolean>;

  accessibility = process.env.NODE_ENV === 'development' && process.env.ACCESSIBILITY === 'true';

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly breakpointsService: BreakpointsService,
    private readonly windowResizeService: WindowResizeService,
    router: Router,
    viewportScroller: ViewportScroller
  ) {
    this.scrollPositionRestoration(router, viewportScroller);
  }

  private scrollPositionRestoration(router: Router, viewportScroller: ViewportScroller): void {
    router.events
      .pipe(
        filter((e: Event) => e instanceof Scroll),
        map((e) => [e as Scroll, this.window.location.pathname]),
        take(1)
      )
      .subscribe({
        next: () => {
          viewportScroller.scrollToPosition([0, 0]);
        },
      });

    router.events
      .pipe(
        filter((e: Event) => e instanceof Scroll),
        map((e) => [e as Scroll, this.window.location.pathname]),
        pairwise()
      )
      .subscribe({
        next: ([[_, previousUrl], [currentEvent, currentUrl]]) => {
          if (currentEvent.position) {
            viewportScroller.scrollToPosition(currentEvent.position);
          } else if (currentEvent.anchor) {
            viewportScroller.scrollToAnchor(currentEvent.anchor);
          } else if (previousUrl !== currentUrl) {
            viewportScroller.scrollToPosition([0, 0]);
          }
        },
      });
  }

  ngOnInit(): void {
    if (this.document.documentElement.lang === '') {
      this.document.documentElement.lang = environment.i18n.lang;
    }

    this.displayFullscreen$ = this.store.select(RouterState.state).pipe(
      map((state: any) => state.data),
      map((data) => !!data.displayFullscreen),
      distinctUntilChanged()
    );

    /**
     * Disable hover on `touchstart` to cover browsers that do not support pointer events.
     * https://caniuse.com/#feat=pointer
     */
    fromEvent(this.window, 'touchstart', { passive: true }).subscribe({
      next: () => {
        this.hoverEnabled = false;
      },
    });

    this.windowResizeService.setWindowSize();

    fromEvent(this.window, 'resize', { passive: true }).subscribe({
      next: () => {
        this.windowResizeService.setWindowSize();
      },
    });

    this.window.setTimeout(() => {
      this.openFeedbackForm();
    }, 1000);
  }

  /**
   * Enable hover if "mouse" pointer event is detected; disable it otherwise.
   * https://developer.mozilla.org/en-US/docs/Web/Events/pointerenter
   */
  @HostListener('pointerenter', ['$event'])
  onPointerEnter(event: any): void {
    this.hoverEnabled = event.pointerType === 'mouse';
  }

  openContactForm(): void {
    this.isMobile$.pipe(take(1)).subscribe({
      next: (isMobile) => {
        this.dialog.open(ContactUsComponent, {
          panelClass: isMobile ? 'wizbii-mobile-panel--white' : 'wizbii-panel--white',
          backdropClass: 'wzb-backdrop',
          minWidth: isMobile ? '100vw' : '50vw',
          autoFocus: false,
          data: {
            isMobile,
            templateKey: 'user_contact_footer',
          },
          disableClose: isMobile,
          position: isMobile ? { bottom: '0' } : {},
        });
      },
    });
  }

  openFeedbackForm(): void {
    combineLatest([this.shouldDisplayModal$, this.isMobile$])
      .pipe(
        take(1),
        filter(([shouldDisplay, _]) => shouldDisplay)
      )
      .subscribe({
        next: ([_, isMobile]) => {
          this.dialog.open(CejModalComponent, {
            panelClass: isMobile ? 'wizbii-mobile-panel--white' : 'wizbii-panel--white',
            disableClose: true,
            backdropClass: 'wzb-backdrop',
            minWidth: isMobile ? '100vw' : '50vw',
            autoFocus: false,
            data: {
              isMobile,
            },
            position: isMobile ? { bottom: '0' } : {},
          });
        },
      });
  }

  @HostListener('document:keydown.tab', ['$event'])
  onKeydownTabHandler(_: KeyboardEvent): void {
    this.accessibilityEnabled = true;
  }

  @HostListener('document:mousedown', ['$event'])
  onMouseDownHandler(_: MouseEvent): void {
    this.accessibilityEnabled = false;
  }
}
